import React, { Component } from 'react';
import PropTypes from 'prop-types';
import TextField from '@material-ui/core/TextField';
import { withStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import red from '@material-ui/core/colors/red';
import { Auth } from 'aws-amplify';
import { Link } from 'react-router-dom';
import queryString from 'query-string';
import {
    validateEmail,
    validatePassword,
    validatePasswordContainsLowerCase,
    validatePasswordContainsUpperCase,
    validatePasswordContainsDigits,
    validatePasswordNoWhitespace
} from '../helpers/validators';
import LoaderButton from '../components/LoaderButton';
import ConfirmationForm from '../components/ConfirmationForm';
import ContentHeader from '../components/ContentHeader';
import globalStyles from '../styles';
import PhoneNumberInput from '../components/PhoneNumberInput';
import { isValidPhoneNumber } from '../helpers/errors';

const styles = theme => ({
    root: {
        background: '#fff',
        boxShadow: '0px 2px 5px 0px rgba(0,0,0,0.21)',
        border: '0',
        maxWidth: '800px',
        margin: '40px auto',
        position: 'relative',
        textAlign: 'center',
        [theme.breakpoints.down('sm')]: {
            maxWidth: '100%',
            margin: '20px 15px'
        }
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '800px',
        margin: '20px auto 40px',
        padding: '0 10px 20px'
    },
    input: {
        margin: '0 20px 10px'
    },
    button: {
        width: '200px',
        height: '56px',
        margin: '0 auto 20px'
    },
    error: {
        margin: '0 20px 10px',
        color: red[500],
        minHeight: '20px'
    },
    login: {
        fontSize: '1em',
        marginBottom: '1em'
    },
    passwordHelp: {
        fontSize: '.8em'
    },
    link: {
        ...globalStyles.link,
        fontWeight: 'bold'
    }
});

class Register extends Component {
    constructor(props) {
        super(props);

        this.state = {
            email: '',
            phone: '',
            password: '',
            confirmPassword: '',
            error: '',
            newUser: null,
            loading: false,
            countryCode: process.env.REACT_APP_DEFAULT_PHONE_COUNTRY || 'GB'
        };
    }

    getLoginLink = () => {
        const { props } = this;
        const qs = queryString.parse(props.location.search, {
            ignoreQueryPrefix: true
        });

        if ('redirect' in qs) {
            return `/login?redirect=${qs.redirect}`;
        }

        return `/login`;
    };

    handleChange = event => {
        this.setState({
            [event.target.id]: event.target.value,
            error: false
        });
    };

    handlePhoneChange = number => {
        this.setState({
            phone: number,
            error: false
        });
    };

    handleCountryCodeChange = code => {
        if (!code) {
            return;
        }

        this.setState({
            countryCode: code,
            error: false
        });
    };

    handleSubmit = async event => {
        event.preventDefault();
        const { email, phone, password } = this.state;

        if (this.validateForm(true)) {
            this.setState({ loading: true });

            try {
                const newUser = await Auth.signUp({
                    username: email.toLowerCase(),
                    password,
                    attributes: {
                        phone_number: phone
                    }
                });
                window.localStorage.setItem(
                    'confirmation_code_sent',
                    new Date().toISOString()
                );
                this.setState({
                    newUser
                });
            } catch (e) {
                this.setState({
                    error: e.message
                });
            }

            this.setState({ loading: false });
        }
    };

    validateForm() {
        const {
            email,
            phone,
            password,
            confirmPassword,
            countryCode
        } = this.state;
        let errorMessage = null;

        if (!validateEmail(email)) {
            errorMessage = 'Please enter a valid email';
        } else if (!isValidPhoneNumber(phone, countryCode)) {
            errorMessage =
                'Please only provide numbers in the phone number and enough digits for a valid number.';
        } else if (!validatePassword(password)) {
            if (password.length < 8) {
                errorMessage = `Your password length is only ${password.length}. It needs to be at least 8.`;
            } else if (!validatePasswordContainsLowerCase(password)) {
                errorMessage = `Your password needs to contain at least 1 lowercase letter.`;
            } else if (!validatePasswordContainsUpperCase(password)) {
                errorMessage = `Your password needs to contain at least 1 uppercase letter.`;
            } else if (!validatePasswordContainsDigits(password)) {
                errorMessage = `Your password needs to contain at least 1 digit.`;
            } else if (!validatePasswordNoWhitespace(password)) {
                errorMessage = `Your password cannot contain whitespace.`;
            } else {
                errorMessage = `Only the following special characters are supported: @ $ ! % * ? & - _ = ' " ~ \` [ ] { } + ^ ( ) : ; , . | / \\ < > #`;
            }
        } else if (password !== confirmPassword) {
            errorMessage = 'Passwords do not match';
        }

        if (errorMessage) {
            this.setState({
                error: errorMessage
            });

            return false;
        }

        return true;
    }

    enableButton() {
        const { email, password, phone, confirmPassword } = this.state;

        return (
            email.length > 0 &&
            password.length > 0 &&
            phone &&
            phone.length > 0 &&
            password === confirmPassword
        );
    }

    renderForm() {
        const { classes } = this.props;
        const {
            email,
            phone,
            password,
            confirmPassword,
            error,
            loading
        } = this.state;

        return (
            <div className={classes.root}>
                <ContentHeader text="Register" id="login-register" />
                <form className={classes.form} onSubmit={this.handleSubmit}>
                    <Typography variant="body1" className={classes.login}>
                        Create an account with ShuttleID.
                    </Typography>
                    <Typography variant="body1" className={classes.login}>
                        You&apos;ll be able to purchase tickets for multiple{' '}
                        passengers using this{' '}
                        <strong>single&nbsp;account</strong>.
                    </Typography>
                    <Typography variant="body1" className={classes.login}>
                        We&apos;ll use the contact details below if there&apos;s
                        an issue with your account or any of your purchases.
                    </Typography>
                    <Typography variant="body1" className={classes.login}>
                        Already have an account?{' '}
                        <Link className={classes.link} to={this.getLoginLink()}>
                            Login
                        </Link>
                        .
                    </Typography>
                    <TextField
                        autoFocus
                        autoComplete="email"
                        className={classes.input}
                        id="email"
                        type="email"
                        label="Your email"
                        value={email}
                        onChange={this.handleChange}
                        variant="outlined"
                    />
                    <PhoneNumberInput
                        number={phone}
                        onChange={this.handlePhoneChange}
                        onCountryChange={this.handleCountryCodeChange}
                        errorMessage={null}
                    />
                    <TextField
                        autoComplete="password"
                        className={classes.input}
                        id="password"
                        type="password"
                        label="Password"
                        value={password}
                        onChange={this.handleChange}
                        variant="outlined"
                    />
                    <TextField
                        autoComplete="password"
                        className={classes.input}
                        id="confirmPassword"
                        type="password"
                        label="Confirm Password"
                        value={confirmPassword}
                        onChange={this.handleChange}
                        variant="outlined"
                    />
                    <Typography
                        variant="body1"
                        className={classes.passwordHelp}
                    >
                        We request passwords to be a minimum 8 characters and to
                        include at least 1 number, 1 lowercase and 1 uppercase
                        letter.
                    </Typography>
                    <Typography className={`error ${classes.error}`}>
                        {error}
                    </Typography>
                    <LoaderButton
                        variant="outlined"
                        color="primary"
                        disabled={!this.enableButton()}
                        className={classes.button}
                        type="submit"
                        isLoading={loading}
                        text="Register"
                        loadingText="Registering.."
                    />
                </form>
            </div>
        );
    }

    render() {
        const { newUser, email, password } = this.state;
        const { handleAuth } = this.props;

        return (
            <div className="Signup">
                {newUser === null ? (
                    this.renderForm()
                ) : (
                    <div className="confirmation">
                        <ConfirmationForm
                            email={email}
                            password={password}
                            handleAuth={handleAuth}
                        />
                    </div>
                )}
            </div>
        );
    }
}

Register.propTypes = {
    classes: PropTypes.shape().isRequired,
    handleAuth: PropTypes.func.isRequired
};

export default withStyles(styles)(Register);
